import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import { noop } from 'lodash'
import HHTextField from 'components/form-fields/v5/HHTextField'
import { ListItem, ListItemText, Stack } from '@mui/material'
import HHDisplayMoney from 'components/HHDisplayMoney'
import { AMOUNT_TYPES } from 'settings/constants/payment'
import SelectItem from 'components/form-fields/v5/SelectItem'
import { createDateFromString, formatDateToFEFormatDateFns } from 'utils/date'
import T from 'T'

const { CURRENT_AMOUNT_DUE, OTHER_AMOUNT } = AMOUNT_TYPES

const HHFormSelectPaymentAmountField = ({
  sx = undefined,
  name,
  control,
  disabled = false,
  label = undefined,
  placeholder = '',
  size = 'medium',
  autocomplete = 'off',
  variant = 'outlined',
  rules = undefined,
  required = false,
  fullWidth = false,
  InputProps = {},
  options,
  getOptionValue = option => option?.value,
  multiple = false,
  onChange = noop,
  SelectProps = {},
  currentAmountDue = 0,
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules ? { ...rules, required: required || rules?.required === true } : { required: required || rules?.required === true }}
      render={({ field }) => {
        const { ref, onChange: onChangeHookForm, onBlur, ..._rest } = field
        const handleChange = !multiple
          ? onChangeHookForm
          : e => {
              e.target.value = typeof value === 'string' ? e.target.value.split(',') : e.target.value
              onChangeHookForm(e)
            }
        const handleChangeWrapper = e => {
          handleChange(e)
          onChange(e.target.value)
        }
        return (
          <HHTextField
            label={label}
            required={required || rules?.required === true}
            sx={sx}
            disabled={disabled}
            placeholder={placeholder}
            size={size}
            autoComplete={autocomplete}
            InputProps={InputProps}
            variant={variant}
            inputRef={ref}
            onChange={handleChangeWrapper}
            onBlur={onBlur}
            {...rest}
            {..._rest}
            fullWidth={fullWidth}
            select
            SelectProps={{ ...SelectProps, multiple }}
          >
            <SelectItem value={CURRENT_AMOUNT_DUE}>
              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <ListItem sx={{ px: 0, py: 0.5, flex: 1 }}>
                  <ListItemText primary="Total balance due" />
                </ListItem>
                <ListItem sx={{ textAlign: 'right', px: 0, py: 0.5, flex: 1 }}>
                  <ListItemText
                    primaryTypographyProps={{ color: 'textSecondary' }}
                    primary={<HHDisplayMoney value={currentAmountDue} formatToDollars={false} />}
                  />
                </ListItem>
              </Stack>
            </SelectItem>
            {options.map(option => {
              const value = getOptionValue(option)
              return (
                <SelectItem disableGutters key={option?.key || `${name}-option-${option.invoiceNumber}`} value={value}>
                  <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary={`Invoice#: ${option.invoiceNumber}`}
                        secondary={`Due date: ${formatDateToFEFormatDateFns(createDateFromString(option.dueDate))}`}
                      />
                    </ListItem>
                    <ListItem sx={{ textAlign: 'right', px: 0, py: 0.5 }}>
                      <ListItemText
                        primaryTypographyProps={{ color: 'textSecondary' }}
                        primary={<HHDisplayMoney value={option.totalCents - option.amountPaidCents} />}
                      />
                    </ListItem>
                  </Stack>
                </SelectItem>
              )
            })}
            <SelectItem value={OTHER_AMOUNT}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText primary={T.OTHER_AMOUNT} />
              </ListItem>
            </SelectItem>
          </HHTextField>
        )
      }}
    />
  )
}

HHFormSelectPaymentAmountField.propTypes = {
  sx: PropTypes.object,
  SelectProps: PropTypes.object,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  autocomplete: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  rules: PropTypes.object,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  InputProps: PropTypes.object,
  options: PropTypes.array.isRequired,
  getOptionValue: PropTypes.func,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  currentAmountDue: PropTypes.number,
}

export default HHFormSelectPaymentAmountField
